import { z } from "zod";

import {
  APPROVAL_KINDS,
  CASE_RESULTS,
  CASE_STATUSES,
  EVIDENCE_KINDS,
  SHIPMENT_DIRECTIONS,
} from "../constants/index.js";

/**
 * 지식 API (harness/KNOWLEDGE-MAP.md §4).
 *
 * 사람이 아니라 **에이전트가 읽는다.** 그래서 목차가 "무엇이 있다" 뿐 아니라
 * **"무엇이 아직 없다"** 도 말한다 — 없는 것을 있는 것처럼 두면, 읽은 쪽은 그것을
 * 사실로 쓰고 그 위에 판단을 쌓는다.
 */
export const KbIndexSchema = z.object({
  ready: z.array(
    z.object({
      key: z.string(),
      path: z.string(),
      what: z.string(),
    }),
  ),
  not_yet: z.array(
    z.object({
      key: z.string(),
      why: z.string(),
    }),
  ),
  counts: z.object({
    cases: z.number().int().nonnegative(),
    devices: z.number().int().nonnegative(),
    swaps: z.number().int().nonnegative(),
    companies: z.number().int().nonnegative(),
  }),
  scope_note: z.string(),
});
export type KbIndex = z.infer<typeof KbIndexSchema>;

/** ★ 문서 없이도 같은 기준으로 움직이게 하는 값들. 원천은 packages/shared 다 */
export const KbRulesSchema = z.object({
  statuses: z.array(z.enum(CASE_STATUSES)),
  results: z.array(z.enum(CASE_RESULTS)),
  approval_kinds: z.array(z.enum(APPROVAL_KINDS)),
  shipment_directions: z.array(z.enum(SHIPMENT_DIRECTIONS)),
  evidence_kinds: z.array(z.enum(EVIDENCE_KINDS)),
  notes: z.array(z.string()),
});
export type KbRules = z.infer<typeof KbRulesSchema>;

/**
 * `/rma/manifest.json` — 단일 진입점 (harness/KNOWLEDGE-MAP.md §2).
 *
 * **인증 없이** 읽힌다. 그래서 데이터는 한 줄도 싣지 않는다 — 구조(어디에 무엇을
 * 물을 수 있는가)만 있다. 건수조차 없다. 건수는 키가 있어야 보는 `/api/kb` 의 몫이다.
 */
export const RmaManifestSchema = z.object({
  name: z.string(),
  publisher: z.string(),
  version: z.string(),
  updated_at: z.string(),
  base: z.string(),
  quickstart: z.string(),
  skills: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      status: z.enum(["available", "partial", "planned"]),
      via: z.string(),
      description: z.string(),
    }),
  ),
  knowledge: z.object({
    index: z.string(),
    ready: z.array(z.object({ key: z.string(), path: z.string(), what: z.string() })),
    not_yet: z.array(z.object({ key: z.string(), why: z.string() })),
  }),
  site_api: z.array(
    z.object({
      path: z.string(),
      what: z.string(),
      scope: z.string(),
      status: z.enum(["available", "planned"]),
    }),
  ),
  mcp: z.object({
    status: z.enum(["available", "planned"]),
    transport: z.string(),
    how: z.string(),
    tools: z.array(z.object({ name: z.string(), what: z.string() })),
  }),
  notes: z.array(z.string()),
});
export type RmaManifest = z.infer<typeof RmaManifestSchema>;
